/**
 * Centralized logging utility for the application.
 *
 * In production, logs are suppressed except for errors.
 * In development, all logs are printed to the console.
 *
 * Usage:
 * ```typescript
 * import { logger } from '@/utils/logger';
 *
 * logger.debug('Debug message', { data });
 * logger.info('Info message', { data });
 * logger.warn('Warning message', { data });
 * logger.error('Error message', error);
 * ```
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enableInProduction: boolean;
  minLevel: LogLevel;
}

const isDevelopment = import.meta.env.DEV;

const config: LoggerConfig = {
  enableInProduction: false,
  minLevel: 'info',
};

const logLevels: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

class Logger {
  private shouldLog(level: LogLevel): boolean {
    // In production, only log errors unless explicitly enabled
    if (!isDevelopment && !config.enableInProduction && level !== 'error') {
      return false;
    }

    // Check if log level meets minimum threshold
    return logLevels[level] >= logLevels[config.minLevel];
  }

  private formatMessage(level: LogLevel, message: string, ...args: unknown[]): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    switch (level) {
      case 'debug':
        console.debug(prefix, message, ...args);
        break;
      case 'info':
        console.info(prefix, message, ...args);
        break;
      case 'warn':
        console.warn(prefix, message, ...args);
        break;
      case 'error':
        console.error(prefix, message, ...args);
        break;
    }
  }

  /**
   * Log debug information (development only by default)
   */
  debug(message: string, ...args: unknown[]): void {
    this.formatMessage('debug', message, ...args);
  }

  /**
   * Log informational messages (development only by default)
   */
  info(message: string, ...args: unknown[]): void {
    this.formatMessage('info', message, ...args);
  }

  /**
   * Log warning messages (development only by default)
   */
  warn(message: string, ...args: unknown[]): void {
    this.formatMessage('warn', message, ...args);
  }

  /**
   * Log error messages (always logged)
   */
  error(message: string, ...args: unknown[]): void {
    this.formatMessage('error', message, ...args);
  }

  /**
   * Configure logger settings
   */
  configure(options: Partial<LoggerConfig>): void {
    Object.assign(config, options);
  }
}

export const logger = new Logger();
